var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NetworkStatus, useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, ButtonBase, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography, } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Chart from "react-apexcharts";
import { AuthContext } from "@/providers/AuthContextProvider";
import { useZonedFormat } from "@/hooks/useZonedFormat";
import { IPermissions } from "@/types/keycloak";
import { getGraphQLActionErrorReason } from "@/services/graphqlActionError";
import { TASK_QUEUES_DEAD_LETTERS, TASK_QUEUES_MESSAGES, TASK_QUEUES_OVERVIEW, TASK_QUEUES_QUERY, TASK_QUEUES_THROUGHPUT, } from "@/queries/TaskQueues";
import { columnField, displayValue, isQueryError, ROW_ID, toGridRows, } from "@/services/ElectoralLogConsole";
import { countTicks, durationSeries, eventLabel, formatAge, formatBytes, GRAPH_PERIODS, GRAPH_RANGES, hasValues, isOutcome, managesDeadLetters, MESSAGE_PAGE_SIZE, MESSAGE_STATES, nextBefore, OUTCOME_HEADER, orderedOutcomes, outcomeColor, outcomeSeries, processedLastHour, selectedMessageIds, } from "@/services/TaskQueues";
var OVERVIEW_POLL_MS = 5000;
var GRAPH_POLL_MS = 30000;
// The worker applies a replay or discard shortly after Harvest queues it.
var REFRESH_AFTER_OPERATION_MS = 2000;
var GRID_HEIGHT = 560;
var CHART_HEIGHT = 280;
var ERROR_HEADER = "x-electoral-log-error";
var readHeaders = { headers: { "x-hasura-role": IPermissions.TASK_QUEUES_READ } };
var writeHeaders = { headers: { "x-hasura-role": IPermissions.TASK_QUEUES_WRITE } };
var queryHeaders = { headers: { "x-hasura-role": IPermissions.TASK_QUEUES_QUERY } };
var DEFAULT_QUERY = "SELECT headers->>'x-step-outcome' AS outcome, count(*) AS messages\nFROM pgmq.a_short_queue\nWHERE archived_at > now() - interval '1 hour'\nGROUP BY 1\nORDER BY 2 DESC";
var NO_SELECTION = { type: "include", ids: new Set() };
var useOutcomeLabel = function () {
    var t = useTranslation().t;
    return useCallback(function (outcome) { return (isOutcome(outcome) ? t("taskQueues.outcomes.".concat(outcome)) : outcome); }, [t]);
};
var OutcomeChips = function (_a) {
    var outcomes = _a.outcomes;
    var label = useOutcomeLabel();
    var entries = orderedOutcomes(outcomes);
    if (entries.length === 0) {
        return _jsx(_Fragment, { children: "\u2014" });
    }
    return (_jsx(Stack, { direction: "row", spacing: 0.5, flexWrap: "wrap", useFlexGap: true, children: entries.map(function (_a) {
            var outcome = _a[0], count = _a[1];
            return (_jsx(Chip, { size: "small", label: "".concat(label(outcome), " ").concat(count.toLocaleString()), sx: { backgroundColor: outcomeColor(outcome), color: "#fff" } }, outcome));
        }) }));
};
var Overview = function (_a) {
    var _b;
    var result = _a.result, selected = _a.selected, onSelect = _a.onSelect;
    var t = useTranslation().t;
    return (_jsx(Box, { sx: { overflowX: "auto" }, children: _jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { children: t("taskQueues.columns.queue") }), _jsx(TableCell, { align: "right", children: t("taskQueues.columns.ready") }), _jsx(TableCell, { align: "right", children: t("taskQueues.columns.runningOrScheduled") }), _jsx(TableCell, { align: "right", children: t("taskQueues.columns.oldest") }), _jsx(TableCell, { align: "right", children: t("taskQueues.columns.processed") }), _jsx(TableCell, { children: t("taskQueues.columns.lastHour") }), _jsx(TableCell, { align: "right", children: t("taskQueues.columns.sent") })] }) }), _jsx(TableBody, { children: ((_b = result === null || result === void 0 ? void 0 : result.queues) !== null && _b !== void 0 ? _b : []).map(function (queue) { return (_jsxs(TableRow, { hover: true, selected: queue.queue === selected, onClick: function () { return onSelect(queue.queue); }, sx: { cursor: "pointer" }, children: [_jsx(TableCell, { children: _jsx(ButtonBase, { "aria-pressed": queue.queue === selected, onClick: function (e) {
                                        e.stopPropagation();
                                        onSelect(queue.queue);
                                    }, sx: { fontFamily: "monospace", fontSize: "inherit" }, children: queue.queue }) }), _jsx(TableCell, { align: "right", children: queue.ready.toLocaleString() }), _jsx(TableCell, { align: "right", children: queue.running_or_scheduled.toLocaleString() }), _jsx(TableCell, { align: "right", children: formatAge(queue.oldest_age_secs) }), _jsx(TableCell, { align: "right", children: processedLastHour(queue).toLocaleString() }), _jsx(TableCell, { children: _jsx(OutcomeChips, { outcomes: queue.last_hour }) }), _jsx(TableCell, { align: "right", children: queue.total_sent.toLocaleString() })] }, queue.queue)); }) })] }) }));
};
var chartOptions = function (base, locale, hours, formatTime) { return (__assign(__assign({}, base), { chart: __assign(__assign({}, base.chart), { toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } }), dataLabels: { enabled: false }, legend: { position: "bottom" }, xaxis: {
        type: "datetime",
        labels: {
            datetimeUTC: false,
            formatter: function (value) {
                return new Date(Number(value)).toLocaleString(locale, __assign(__assign({}, (hours > 24 ? { month: "short", day: "numeric" } : {})), { hour: "2-digit", minute: "2-digit" }));
            },
        },
    }, tooltip: {
        x: {
            formatter: formatTime,
        },
    } })); };
var Graphs = function (_a) {
    var _b, _c;
    var queue = _a.queue, live = _a.live;
    var _d = useTranslation(), t = _d.t, i18n = _d.i18n;
    var outcomeLabel = useOutcomeLabel();
    var locale = (_b = i18n.resolvedLanguage) !== null && _b !== void 0 ? _b : i18n.language;
    var zoned = useZonedFormat(null);
    var formatTime = function (value) { return zoned.format(value); };
    var _e = useState("day"), period = _e[0], setPeriod = _e[1];
    var range = GRAPH_RANGES[period];
    var _f = useQuery(TASK_QUEUES_THROUGHPUT, {
        variables: { queue: queue, hours: range.hours, bucketMinutes: range.bucketMinutes },
        fetchPolicy: "network-only",
        pollInterval: live ? GRAPH_POLL_MS : 0,
        // Each poll then renders, also when its result is unchanged, so the window moves.
        notifyOnNetworkStatusChange: true,
        context: readHeaders,
    }), data = _f.data, error = _f.error, networkStatus = _f.networkStatus;
    var result = data === null || data === void 0 ? void 0 : data.task_queues_throughput;
    var _g = useState(function () { return new Date(); }), now = _g[0], setNow = _g[1];
    useEffect(function () {
        if (result && networkStatus === NetworkStatus.ready) {
            setNow(new Date());
        }
    }, [result, networkStatus]);
    var outcomes = result ? outcomeSeries(result, now) : [];
    var durations = result ? durationSeries(result, now) : null;
    var showsDurations = durations !== null && (hasValues(durations.wait) || hasValues(durations.processing));
    return (_jsxs(Stack, { spacing: 2, children: [_jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", justifyContent: "space-between", children: [_jsx(Typography, { variant: "h6", children: t("taskQueues.graphs.title") }), _jsxs(FormControl, { size: "small", sx: { minWidth: 160 }, children: [_jsx(InputLabel, { id: "task-queues-period", children: t("taskQueues.graphs.period") }), _jsx(Select, { labelId: "task-queues-period", label: t("taskQueues.graphs.period"), value: period, onChange: function (e) { return setPeriod(e.target.value); }, children: GRAPH_PERIODS.map(function (name) { return (_jsx(MenuItem, { value: name, children: t("taskQueues.graphs.periods.".concat(name)) }, name)); }) })] })] }), error && (_jsx(Alert, { severity: "error", children: (_c = getGraphQLActionErrorReason(error)) !== null && _c !== void 0 ? _c : t("taskQueues.error") })), result && outcomes.length === 0 && networkStatus !== NetworkStatus.loading && (_jsx(Alert, { severity: "info", children: t("taskQueues.graphs.empty") })), result && outcomes.length > 0 && durations && (_jsxs(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: showsDurations ? "1fr 1fr" : "1fr" },
                    gap: 2,
                }, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle2", children: t("taskQueues.graphs.outcomes") }), _jsx(Chart, { type: "bar", height: CHART_HEIGHT, series: outcomes.map(function (series) { return (__assign(__assign({}, series), { name: outcomeLabel(series.name) })); }), options: chartOptions({
                                    chart: { stacked: true },
                                    colors: outcomes.map(function (series) { return outcomeColor(series.name); }),
                                    plotOptions: { bar: { columnWidth: "90%" } },
                                    yaxis: {
                                        min: 0,
                                        tickAmount: countTicks(outcomes),
                                        forceNiceScale: true,
                                        labels: { formatter: function (value) { return String(Math.round(value)); } },
                                    },
                                }, locale, range.hours, formatTime) }, locale)] }), showsDurations && (_jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle2", children: t("taskQueues.graphs.durations") }), _jsx(Chart, { type: "line", height: CHART_HEIGHT, series: [
                                    { name: t("taskQueues.graphs.wait"), data: durations.wait },
                                    {
                                        name: t("taskQueues.graphs.processing"),
                                        data: durations.processing,
                                    },
                                ], options: chartOptions({
                                    stroke: { width: 2 },
                                    markers: { size: 3 },
                                    yaxis: {
                                        title: { text: t("taskQueues.graphs.seconds") },
                                        labels: {
                                            formatter: function (value) {
                                                return value === null
                                                    ? ""
                                                    : value.toLocaleString(locale);
                                            },
                                        },
                                    },
                                }, locale, range.hours, formatTime) }, locale)] }))] }))] }));
};
var ConfirmDialog = function (_a) {
    var operation = _a.operation, count = _a.count, onCancel = _a.onCancel, onConfirm = _a.onConfirm;
    var t = useTranslation().t;
    return (_jsx(Dialog, { open: operation !== null, onClose: onCancel, children: operation && (_jsxs(_Fragment, { children: [_jsx(DialogTitle, { children: t("taskQueues.deadLetters.confirmTitle.".concat(operation)) }), _jsx(DialogContent, { children: _jsx(DialogContentText, { children: t("taskQueues.deadLetters.confirmBody.".concat(operation), {
                            count: count,
                        }) }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: onCancel, children: t("taskQueues.deadLetters.cancel") }), _jsx(Button, { variant: "contained", color: operation === "discard" ? "error" : "primary", onClick: onConfirm, children: t("taskQueues.deadLetters.".concat(operation)) })] })] })) }));
};
var Messages = function (_a) {
    var _b, _c, _d;
    var queue = _a.queue, canWrite = _a.canWrite;
    var t = useTranslation().t;
    var outcomeLabel = useOutcomeLabel();
    var zoned = useZonedFormat(null, { seconds: true });
    var _e = useState("queued"), state = _e[0], setState = _e[1];
    // The `before` cursor of each page shown so far; the last is the current page.
    var _f = useState([null]), cursors = _f[0], setCursors = _f[1];
    var _g = useState(NO_SELECTION), selection = _g[0], setSelection = _g[1];
    var _h = useState(null), operation = _h[0], setOperation = _h[1];
    // The events chosen when the dialog opened; a refresh may clear the selection meanwhile.
    var _j = useState([]), operationIds = _j[0], setOperationIds = _j[1];
    var _k = useState(null), notice = _k[0], setNotice = _k[1];
    var _l = useState(null), failure = _l[0], setFailure = _l[1];
    var before = cursors[cursors.length - 1];
    useEffect(function () {
        setCursors([null]);
        setSelection(NO_SELECTION);
        setNotice(null);
        setFailure(null);
    }, [queue, state]);
    // A refresh scheduled after an operation belongs to the queue and list it was made on.
    var refreshTimer = useRef(undefined);
    // Counts list changes, so that an operation answered after one leaves the new list alone.
    var listVersion = useRef(0);
    useEffect(function () { return function () {
        window.clearTimeout(refreshTimer.current);
        listVersion.current += 1;
    }; }, [queue, state]);
    // A selection belongs to the page it was made on.
    useEffect(function () {
        setSelection(NO_SELECTION);
    }, [before]);
    var _m = useQuery(TASK_QUEUES_MESSAGES, {
        variables: { queue: queue, state: state, before: before, limit: MESSAGE_PAGE_SIZE },
        fetchPolicy: "network-only",
        context: readHeaders,
    }), data = _m.data, error = _m.error, loading = _m.loading, refetch = _m.refetch;
    var _o = useMutation(TASK_QUEUES_DEAD_LETTERS, {
        context: writeHeaders,
    }), applyOperation = _o[0], applying = _o[1].loading;
    var messages = (_c = (_b = data === null || data === void 0 ? void 0 : data.task_queues_messages) === null || _b === void 0 ? void 0 : _b.messages) !== null && _c !== void 0 ? _c : [];
    var manages = managesDeadLetters(queue, state);
    var selected = selectedMessageIds(selection, messages);
    var older = nextBefore(messages, MESSAGE_PAGE_SIZE);
    var showsEvents = messages.some(function (message) { return Object.keys(message.event).length > 0; });
    var showsErrors = messages.some(function (message) { return message.headers[ERROR_HEADER]; });
    var refresh = function () { return __awaiter(void 0, void 0, void 0, function () {
        var refreshFailure_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setSelection(NO_SELECTION);
                    setFailure(null);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, refetch()];
                case 2:
                    _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    refreshFailure_1 = _b.sent();
                    setFailure((_a = getGraphQLActionErrorReason(refreshFailure_1)) !== null && _a !== void 0 ? _a : t("taskQueues.error"));
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var confirm = function () { return __awaiter(void 0, void 0, void 0, function () {
        var ids, version, answer, result, operationFailure_1;
        var _a, _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    if (!operation) {
                        return [2 /*return*/];
                    }
                    ids = operationIds;
                    version = listVersion.current;
                    setOperation(null);
                    setNotice(null);
                    setFailure(null);
                    _d.label = 1;
                case 1:
                    _d.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, applyOperation({
                            variables: { operation: operation, messageIds: ids.map(String) },
                        })];
                case 2:
                    answer = (_d.sent()).data;
                    result = answer === null || answer === void 0 ? void 0 : answer.task_queues_dead_letters;
                    setNotice(t("taskQueues.deadLetters.queued.".concat(operation), {
                        count: (_a = result === null || result === void 0 ? void 0 : result.requested) !== null && _a !== void 0 ? _a : ids.length,
                        taskId: (_b = result === null || result === void 0 ? void 0 : result.task_id) !== null && _b !== void 0 ? _b : "",
                    }));
                    if (version !== listVersion.current) {
                        return [2 /*return*/];
                    }
                    setSelection(NO_SELECTION);
                    window.clearTimeout(refreshTimer.current);
                    refreshTimer.current = window.setTimeout(function () {
                        refresh();
                    }, REFRESH_AFTER_OPERATION_MS);
                    return [3 /*break*/, 4];
                case 3:
                    operationFailure_1 = _d.sent();
                    setFailure((_c = getGraphQLActionErrorReason(operationFailure_1)) !== null && _c !== void 0 ? _c : t("taskQueues.deadLetters.failed"));
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    // Built again only when its inputs change, so the grid keeps column widths the user set.
    var columns = useMemo(function () {
        var date = function (value) { return (value ? zoned.format(value) : "—"); };
        return __spreadArray(__spreadArray(__spreadArray(__spreadArray([
            { field: "msg_id", headerName: t("taskQueues.messages.columns.id"), width: 90 },
            {
                field: "task",
                headerName: t("taskQueues.messages.columns.task"),
                minWidth: 240,
                flex: 1,
                valueGetter: function (_, row) { var _a; return (_a = row.task) !== null && _a !== void 0 ? _a : t("taskQueues.messages.unreadable"); },
            }
        ], (state === "archived"
            ? [
                {
                    field: "outcome",
                    headerName: t("taskQueues.messages.columns.outcome"),
                    width: 130,
                    valueGetter: function (_, row) { var _a; return (_a = row.headers[OUTCOME_HEADER]) !== null && _a !== void 0 ? _a : "unknown"; },
                    renderCell: function (_a) {
                        var value = _a.value;
                        return (_jsx(Chip, { size: "small", label: outcomeLabel(value !== null && value !== void 0 ? value : "unknown"), sx: {
                                backgroundColor: outcomeColor(value !== null && value !== void 0 ? value : "unknown"),
                                color: "#fff",
                            } }));
                    },
                },
            ]
            : []), true), (showsEvents
            ? [
                {
                    field: "event",
                    headerName: t("taskQueues.messages.columns.event"),
                    minWidth: 260,
                    flex: 1,
                    valueGetter: function (_, row) { return eventLabel(row.event); },
                },
            ]
            : []), true), (showsErrors
            ? [
                {
                    field: "error",
                    headerName: t("taskQueues.messages.columns.error"),
                    minWidth: 240,
                    flex: 1,
                    valueGetter: function (_, row) { var _a; return (_a = row.headers[ERROR_HEADER]) !== null && _a !== void 0 ? _a : ""; },
                    renderCell: function (_a) {
                        var value = _a.value;
                        return (_jsx("span", { title: value, children: value }));
                    },
                },
            ]
            : []), true), [
            {
                field: "enqueued_at",
                headerName: t("taskQueues.messages.columns.enqueued"),
                width: 220,
                valueFormatter: function (value) { return date(value); },
            },
            {
                field: "read_count",
                headerName: t("taskQueues.messages.columns.reads"),
                width: 80,
                type: "number",
            },
            {
                field: "retries",
                headerName: t("taskQueues.messages.columns.retries"),
                width: 80,
                type: "number",
            },
            state === "archived"
                ? {
                    field: "archived_at",
                    headerName: t("taskQueues.messages.columns.archived"),
                    width: 220,
                    valueFormatter: function (value) { return date(value); },
                }
                : {
                    field: "visible_at",
                    headerName: t("taskQueues.messages.columns.visible"),
                    width: 220,
                    valueFormatter: function (value) { return date(value); },
                },
            {
                field: "size_bytes",
                headerName: t("taskQueues.messages.columns.size"),
                width: 100,
                type: "number",
                valueFormatter: function (value) { return formatBytes(value); },
            },
            {
                field: "task_id",
                headerName: t("taskQueues.messages.columns.taskId"),
                minWidth: 290,
            },
        ], false);
    }, [state, showsEvents, showsErrors, t, zoned, outcomeLabel]);
    return (_jsxs(Stack, { spacing: 2, children: [_jsxs(Stack, { direction: { xs: "column", sm: "row" }, spacing: 2, alignItems: { sm: "center" }, justifyContent: "space-between", children: [_jsx(Typography, { variant: "h6", children: t("taskQueues.messages.title") }), _jsx(Tabs, { value: state, onChange: function (_, value) { return setState(value); }, children: MESSAGE_STATES.map(function (name) { return (_jsx(Tab, { value: name, label: t("taskQueues.messages.states.".concat(name)) }, name)); }) })] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: t("taskQueues.messages.argumentsHidden") }), manages && (_jsx(Alert, { severity: "info", children: canWrite
                    ? t("taskQueues.deadLetters.help")
                    : t("taskQueues.deadLetters.noWrite") })), notice && (_jsx(Alert, { severity: "success", onClose: function () { return setNotice(null); }, children: notice })), (failure || error) && (_jsx(Alert, { severity: "error", children: (_d = failure !== null && failure !== void 0 ? failure : getGraphQLActionErrorReason(error)) !== null && _d !== void 0 ? _d : t("taskQueues.error") })), _jsxs(Stack, { direction: "row", spacing: 1, flexWrap: "wrap", useFlexGap: true, children: [_jsx(Button, { onClick: refresh, children: t("taskQueues.messages.refresh") }), _jsx(Button, { disabled: cursors.length === 1, onClick: function () { return setCursors([null]); }, children: t("taskQueues.messages.newest") }), _jsx(Button, { disabled: cursors.length === 1, onClick: function () { return setCursors(cursors.slice(0, -1)); }, children: t("taskQueues.messages.newer") }), _jsx(Button, { disabled: !older, onClick: function () { return setCursors(__spreadArray(__spreadArray([], cursors, true), [older], false)); }, children: t("taskQueues.messages.older") }), manages && canWrite && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "contained", disabled: selected.length === 0 || applying, onClick: function () {
                                    setOperationIds(selected);
                                    setOperation("replay");
                                }, children: t("taskQueues.deadLetters.replay") }), _jsx(Button, { variant: "outlined", color: "error", disabled: selected.length === 0 || applying, onClick: function () {
                                    setOperationIds(selected);
                                    setOperation("discard");
                                }, children: t("taskQueues.deadLetters.discard") })] }))] }), _jsx(Box, { sx: { height: GRID_HEIGHT, width: "100%" }, children: _jsx(DataGrid, { rows: messages, columns: columns, getRowId: function (row) { return row.msg_id; }, loading: loading, checkboxSelection: manages && canWrite, rowSelectionModel: selection, onRowSelectionModelChange: setSelection, disableRowSelectionOnClick: true, hideFooterPagination: true, initialState: { columns: { columnVisibilityModel: { task_id: false } } }, localeText: { noRowsLabel: t("taskQueues.messages.empty") } }) }), _jsx(ConfirmDialog, { operation: operation, count: operationIds.length, onCancel: function () { return setOperation(null); }, onConfirm: confirm })] }));
};
var queryColumns = function (_a) {
    var columns = _a.columns;
    return columns.map(function (column, index) { return ({
        field: columnField(index),
        headerName: column,
        minWidth: 120,
        flex: 1,
        sortable: false,
        valueFormatter: function (value) { return displayValue(value); },
    }); });
};
var QueryPanel = function () {
    var t = useTranslation().t;
    var client = useApolloClient();
    var _a = useState(DEFAULT_QUERY), sql = _a[0], setSql = _a[1];
    var _b = useState(null), result = _b[0], setResult = _b[1];
    var _c = useState(null), error = _c[0], setError = _c[1];
    var _d = useState(false), running = _d[0], setRunning = _d[1];
    var run = function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, answer, failure_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (running || !sql.trim()) {
                        return [2 /*return*/];
                    }
                    setRunning(true);
                    setError(null);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, client.query({
                            query: TASK_QUEUES_QUERY,
                            variables: { sql: sql },
                            fetchPolicy: "no-cache",
                            context: queryHeaders,
                        })];
                case 2:
                    data = (_b.sent()).data;
                    answer = data === null || data === void 0 ? void 0 : data.task_queues_query;
                    if (isQueryError(answer)) {
                        setResult(null);
                        setError(answer.error);
                    }
                    else {
                        setResult(answer);
                    }
                    return [3 /*break*/, 5];
                case 3:
                    failure_1 = _b.sent();
                    setResult(null);
                    setError((_a = getGraphQLActionErrorReason(failure_1)) !== null && _a !== void 0 ? _a : t("taskQueues.query.error"));
                    return [3 /*break*/, 5];
                case 4:
                    setRunning(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var columns = useMemo(function () { return (result ? queryColumns(result) : []); }, [result]);
    var rows = useMemo(function () { return (result ? toGridRows(result) : []); }, [result]);
    return (_jsxs(Stack, { spacing: 2, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("taskQueues.query.help") }), _jsx(Alert, { severity: "warning", children: t("taskQueues.query.arguments") }), _jsx(TextField, { multiline: true, minRows: 6, fullWidth: true, value: sql, placeholder: t("taskQueues.query.placeholder"), onChange: function (e) { return setSql(e.target.value); }, onKeyDown: function (e) {
                    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                        run();
                    }
                }, inputProps: {
                    "aria-label": t("taskQueues.tabs.query"),
                    "spellCheck": false,
                    "style": { fontFamily: "monospace", fontSize: 13 },
                } }), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", justifyContent: "space-between", children: [_jsx(Typography, { variant: "caption", color: "text.secondary", children: t("taskQueues.query.limits") }), _jsx(Button, { variant: "contained", onClick: run, disabled: running || !sql.trim(), children: t("taskQueues.query.run") })] }), error && (_jsx(Alert, { severity: "error", sx: { whiteSpace: "pre-wrap", fontFamily: "monospace" }, children: error })), result && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("taskQueues.query.summary", {
                            rows: result.rows.length.toLocaleString(),
                            ms: result.elapsed_ms.toLocaleString(),
                        }) }), result.truncated && (_jsx(Alert, { severity: "info", children: t("taskQueues.query.truncated", {
                            rows: result.rows.length.toLocaleString(),
                        }) })), _jsx(Box, { sx: { height: GRID_HEIGHT, width: "100%" }, children: _jsx(DataGrid, { rows: rows, columns: columns, getRowId: function (row) { return row[ROW_ID]; }, loading: running, initialState: { pagination: { paginationModel: { pageSize: 25 } } }, pageSizeOptions: [25, 50, 100], disableRowSelectionOnClick: true, showToolbar: true, slotProps: {
                                toolbar: {
                                    showQuickFilter: false,
                                    csvOptions: { fileName: "task-queues-query" },
                                    printOptions: { disableToolbarButton: true },
                                },
                            } }) })] }))] }));
};
export var TaskQueues = function () {
    var _a;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var authContext = useContext(AuthContext);
    var locale = (_a = i18n.resolvedLanguage) !== null && _a !== void 0 ? _a : i18n.language;
    var canRead = authContext.isAuthorized(true, null, IPermissions.TASK_QUEUES_READ);
    var canWrite = authContext.isAuthorized(true, null, IPermissions.TASK_QUEUES_WRITE);
    var canQuery = authContext.isAuthorized(true, null, IPermissions.TASK_QUEUES_QUERY);
    var _c = useState("queues"), tab = _c[0], setTab = _c[1];
    var _d = useState(true), live = _d[0], setLive = _d[1];
    var _e = useState(null), queue = _e[0], setQueue = _e[1];
    var _f = useQuery(TASK_QUEUES_OVERVIEW, {
        fetchPolicy: "network-only",
        // Each poll then renders, also when its result is unchanged, so the time moves.
        notifyOnNetworkStatusChange: true,
        pollInterval: live && tab === "queues" ? OVERVIEW_POLL_MS : 0,
        skip: !canRead || tab !== "queues",
        context: readHeaders,
    }), data = _f.data, error = _f.error, networkStatus = _f.networkStatus;
    var overview = data === null || data === void 0 ? void 0 : data.task_queues_overview;
    var _g = useState(null), updatedAt = _g[0], setUpdatedAt = _g[1];
    useEffect(function () {
        if (overview && networkStatus === NetworkStatus.ready) {
            setUpdatedAt(new Date());
        }
    }, [overview, networkStatus]);
    useEffect(function () {
        if (!queue && overview && overview.queues.length > 0) {
            setQueue(overview.queues[0].queue);
        }
    }, [queue, overview]);
    if (!canRead) {
        return _jsx(Alert, { severity: "warning", children: t("taskQueues.notAllowed") });
    }
    return (
    // The page takes the width the layout gives it, whatever its tables' width.
    _jsx(Card, { sx: { contain: "inline-size" }, children: _jsx(CardContent, { children: _jsxs(Stack, { spacing: 3, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "h4", sx: { mb: 1 }, children: t("taskQueues.title") }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: t("taskQueues.subtitle") })] }), canQuery && (_jsxs(Tabs, { value: tab, onChange: function (_, value) { return setTab(value); }, children: [_jsx(Tab, { value: "queues", label: t("taskQueues.tabs.queues") }), _jsx(Tab, { value: "query", label: t("taskQueues.tabs.query") })] })), tab === "query" && canQuery ? (_jsx(QueryPanel, {})) : (_jsx(QueuesPanel, { live: live, setLive: setLive, updatedAt: updatedAt, locale: locale, error: error, overview: overview, queue: queue, setQueue: setQueue, canWrite: canWrite }))] }) }) }));
};
var QueuesPanel = function (_a) {
    var _b;
    var live = _a.live, setLive = _a.setLive, updatedAt = _a.updatedAt, locale = _a.locale, error = _a.error, overview = _a.overview, queue = _a.queue, setQueue = _a.setQueue, canWrite = _a.canWrite;
    var t = useTranslation().t;
    return (_jsxs(Stack, { spacing: 3, children: [_jsxs(Stack, { direction: "row", spacing: 2, alignItems: "center", justifyContent: "space-between", children: [_jsx(FormControlLabel, { control: _jsx(Switch, { checked: live, onChange: function (e) { return setLive(e.target.checked); } }), label: t("taskQueues.live") }), updatedAt && (_jsx(Typography, { variant: "caption", color: "text.secondary", children: t("taskQueues.updated", {
                            time: updatedAt.toLocaleTimeString(locale),
                        }) }))] }), error && (_jsx(Alert, { severity: "error", children: (_b = getGraphQLActionErrorReason(error)) !== null && _b !== void 0 ? _b : t("taskQueues.error") })), _jsx(Overview, { result: overview, selected: queue, onSelect: setQueue }), queue && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h5", sx: { fontFamily: "monospace" }, children: queue }), _jsx(Graphs, { queue: queue, live: live }), _jsx(Messages, { queue: queue, canWrite: canWrite })] }))] }));
};
